'use client'

import { GalleryBlock as GalleryBlockTypes } from '@/payload-types'
import { Media } from '@onclicks/sharedcomponents/Media/Media'
import { cn } from '@/utilities/ui'
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

const AutoScrollingGallery: React.FC<{
  images: GalleryBlockTypes['images']
}> = ({ images }) => {
  const slideRef = useRef<HTMLDivElement>(null)
  const [slideWidth, setSlideWidth] = useState(0)
  const MotionDiv = motion.div

  useEffect(() => {
    if (slideRef.current) {
      setSlideWidth(slideRef.current.offsetWidth)
    }
  }, [images])

  if (!images) return null

  return (
    <div className="slider overflow-hidden w-full py-10">
      <Suspense fallback={<div className="flex w-max" />}>
        <div className="track">
          {[...images, ...images, ...images].map(
            (media, index) =>
              media &&
              typeof media === 'object' && (
                <div
                  key={`auto-${media.id}-${index}`}
                  ref={index === 0 ? slideRef : null}
                  className="shrink-0 w-[500px] px-6 [clip-path:polygon(17%_0%,90%_0%,83%_100%,10%_100%)]"
                >
                  <Media imgClassName="w-full aspect-video object-cover " resource={media.image} />
                  <h4 className="text-center font-semibold my-5 pr-3.5">{media.title}</h4>
                  <p className="text-sm text-center">{media.imageText}</p>
                </div>
              ),
          )}
        </div>
      </Suspense>
    </div>
  )
}

const GalleryBlock: React.FC<GalleryBlockTypes> = (props) => {
  const { images, title, anchor, variant = 'default' } = props

  const containerClasses = {
    default: '',
    light_center: 'bg-transparent text-black text-center mt-10 lg:mt-20 mb-10',
    color_hover: 'w-full text-white',
    auto_scrolling: 'w-full',
  }

  const galleryClasses = {
    default: '',
    light_center: 'w-9/10 items-start',
    color_hover: '',
    auto_scrolling: 'w-full items-center',
  }

  const imagesSize = {
    default: '',
    light_center: 'flex flex-col items-center text-center w-64 mx-auto',
    color_hover: 'w-40 h-32 mx-auto flex items-center justify-center rounded-lg p-3',
    auto_scrolling: 'flex flex-col items-center text-center',
  }

  const imageStyle = {
    default: '',
    light_center: '',
    color_hover:
      'max-w-full h-10 object-contain grayscale brightness-[999] hover:grayscale-0 hover:brightness-100 rounded-lg',
    auto_scrolling: '',
  }

  return (
    <>
      <div
        id={(anchor?.value && typeof anchor.value === 'object' && anchor?.value.anchor) || ''}
        className={`
         ${containerClasses[variant || 'default']}
      `}
      >
        <div className="flex flex-col items-center justify-center">
          {title && <h2 className={`mt-6 text-center px-4 md:px-0`}> {title} </h2>}

          {images && variant === 'auto_scrolling' && <AutoScrollingGallery images={images} />}

          {/* Code de gallerie classique */}
          {images && variant !== 'auto_scrolling' && (
            <div
              className={cn(
                'flex flex-wrap gap-y-8 py-5 mt-2 mb-8',
                galleryClasses[variant || 'default'],
                images.length == 1 ? 'justify-center' : 'lg:justify-around sm:justify-center',
              )}
            >
              {images.map(
                (media) =>
                  media &&
                  typeof media === 'object' && (
                    <div key={media.id} className={imagesSize[variant || 'default']}>
                      {/* Image par dÃ©faut */}
                      <Media
                        imgClassName={imageStyle[variant || 'default']}
                        resource={media.image}
                      />

                      <h4 className="font-semibold my-5 dark:text-white">{media.title}</h4>
                      <p className="text-sm dark:text-white">{media.imageText}</p>
                    </div>
                  ),
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export { GalleryBlock }
